import React, { useState } from "react";
import { Youtube, Plus, Pencil, Trash2, Play, ExternalLink } from "lucide-react";
import {
  AppButton, AppCard, AppCardContent, AppCardHeader, AppCardTitle, AppModal,
  Callout, ConfirmModal, EmptyState, FormField, IconButton, Input, NativeSelect,
  Textarea,
} from "../ui";
import { videoThumbnail } from "./constants";
import { videosApi, errorText } from "./portfolioApi";

/**
 * VideosSection — project showcase videos.
 *
 * ⚠️ Videos are LINKED, never embedded on load, and never uploaded here. Three
 * reasons, in order of how much they matter: an autoplaying third-party iframe
 * per card would load YouTube's player and its cookies for every visitor to a
 * student's portfolio; the thumbnail is a single image request against the id
 * the server already extracted; and hosting video would put GB-scale files in
 * the institute's storage bill for something YouTube does for free.
 *
 * The card is a link to the video. `videoId` comes from the server
 * (utils/videoLinks.js parses the six YouTube URL shapes once, on write), so
 * nothing here parses a URL it did not validate.
 */

const EMPTY = { title: "", url: "", description: "", projectId: "" };

const toForm = (v) => ({
  title: v.title || "",
  url: v.url || "",
  description: v.description || "",
  projectId: v.projectId || "",
});

const VideosSection = ({ videos = [], projects = [], onPortfolioChange, readOnly = false }) => {
  const [modal, setModal] = useState(null);
  const [confirm, setConfirm] = useState(null);
  const [busy, setBusy] = useState(false);

  const projectName = (id) => projects.find((p) => p._id === id)?.title || "";

  const del = async () => {
    setBusy(true);
    try {
      const { portfolio } = await videosApi.remove(confirm._id);
      onPortfolioChange(portfolio, "Video removed.");
      setConfirm(null);
    } catch (err) {
      onPortfolioChange(null, errorText(err, "Failed to remove the video."), "danger");
    } finally {
      setBusy(false);
    }
  };

  return (
    <AppCard>
      <AppCardHeader className="tw-flex-row tw-items-start tw-justify-between tw-gap-3">
        <div className="tw-min-w-0">
          <AppCardTitle className="[&_svg]:tw-text-primary">
            <Youtube size={18} /> Video showcase
            {videos.length > 0 && (
              <span className="tw-ml-1 tw-text-sm tw-font-normal tw-text-content-subtle">
                ({videos.length})
              </span>
            )}
          </AppCardTitle>
          <p className="tw-m-0 tw-mt-1 tw-text-[0.8rem] tw-leading-relaxed tw-text-content-muted">
            Demos and walkthroughs. A two-minute video of your project working beats a paragraph
            describing it.
          </p>
        </div>
        {!readOnly && (
          <AppButton variant="outline" size="sm" onClick={() => setModal({ mode: "add" })}>
            <Plus size={14} /> Add video
          </AppButton>
        )}
      </AppCardHeader>

      <AppCardContent>
        {videos.length === 0 ? (
          <EmptyState
            icon={Youtube}
            title="No videos yet"
            description={readOnly
              ? "This student has not added any showcase videos."
              : "Paste a YouTube or Vimeo link to a demo, a walkthrough or a talk."}
            action={!readOnly && (
              <AppButton size="sm" onClick={() => setModal({ mode: "add" })}>
                <Plus size={14} /> Add a video
              </AppButton>
            )}
          />
        ) : (
          <ul className="tw-m-0 tw-grid tw-list-none tw-gap-3 tw-p-0 min-[640px]:tw-grid-cols-2 min-[1100px]:tw-grid-cols-3">
            {videos.map((v) => {
              const thumb = videoThumbnail(v);
              return (
                <li
                  key={v._id}
                  className="tw-group tw-flex tw-flex-col tw-overflow-hidden tw-rounded-md tw-border tw-border-solid tw-border-line-soft tw-bg-surface-sunken tw-transition-colors hover:tw-border-primary/40"
                >
                  <a
                    href={v.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="tw-relative tw-block tw-aspect-video tw-overflow-hidden tw-bg-surface-hover tw-no-underline"
                    aria-label={`Play ${v.title} (opens in a new tab)`}
                  >
                    {thumb ? (
                      <img src={thumb} alt="" loading="lazy"
                        className="tw-h-full tw-w-full tw-object-cover tw-transition-transform tw-duration-300 group-hover:tw-scale-105 motion-reduce:tw-transition-none motion-reduce:group-hover:tw-scale-100" />
                    ) : (
                      <span className="tw-flex tw-h-full tw-w-full tw-items-center tw-justify-center tw-text-content-subtle">
                        <ExternalLink size={26} />
                      </span>
                    )}
                    <span className="tw-absolute tw-inset-0 tw-grid tw-place-items-center tw-bg-surface-dark/35 tw-opacity-0 tw-transition-opacity group-hover:tw-opacity-100">
                      <span className="tw-grid tw-h-12 tw-w-12 tw-place-items-center tw-rounded-full tw-bg-on-surface-dark/95 tw-text-surface-dark">
                        <Play size={20} className="tw-fill-current tw-translate-x-0.5" />
                      </span>
                    </span>
                  </a>

                  <div className="tw-flex tw-flex-1 tw-flex-col tw-gap-1 tw-p-3">
                    <div className="tw-flex tw-items-start tw-justify-between tw-gap-2">
                      <h5 className="tw-m-0 tw-text-[0.875rem] tw-font-semibold tw-leading-snug tw-text-content">
                        {v.title}
                      </h5>
                      {!readOnly && (
                        <div className="tw-flex tw-shrink-0 tw-gap-0.5">
                          <IconButton label="Edit video" icon={Pencil} size="xs"
                            onClick={() => setModal({ mode: "edit", item: v })} />
                          <IconButton label="Remove video" icon={Trash2} size="xs"
                            variant="ghost-destructive" onClick={() => setConfirm(v)} />
                        </div>
                      )}
                    </div>
                    {v.projectId && projectName(v.projectId) && (
                      <p className="tw-m-0 tw-text-[0.7rem] tw-font-medium tw-text-primary">
                        {projectName(v.projectId)}
                      </p>
                    )}
                    {v.description && (
                      <p className="tw-m-0 tw-text-[0.78rem] tw-leading-relaxed tw-text-content-muted">
                        {v.description}
                      </p>
                    )}
                  </div>
                </li>
              );
            })}
          </ul>
        )}
      </AppCardContent>

      {modal && (
        <VideoModal
          mode={modal.mode}
          item={modal.item}
          projects={projects}
          onClose={() => setModal(null)}
          onSaved={(portfolio, message) => { onPortfolioChange(portfolio, message); setModal(null); }}
        />
      )}

      <ConfirmModal
        show={!!confirm}
        onHide={() => !busy && setConfirm(null)}
        onConfirm={del}
        loading={busy}
        variant="danger"
        title="Remove this video?"
        message={confirm ? `"${confirm.title}" will be removed from your showcase.` : ""}
        confirmText="Remove"
      />
    </AppCard>
  );
};

const VideoModal = ({ mode, item, projects, onClose, onSaved }) => {
  const [form, setForm] = useState(() => (item ? toForm(item) : EMPTY));
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const set = (patch) => setForm((f) => ({ ...f, ...patch }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.title.trim()) { setError("A title is required."); return; }
    if (!form.url.trim()) { setError("Paste the video link."); return; }
    setSaving(true);
    setError("");
    try {
      const { portfolio, message } = mode === "add"
        ? await videosApi.add(form)
        : await videosApi.update(item._id, form);
      onSaved(portfolio, message);
    } catch (err) {
      setError(errorText(err, "Failed to save the video."));
      setSaving(false);
    }
  };

  return (
    <AppModal
      show
      onHide={onClose}
      title={mode === "add" ? "Add a showcase video" : "Edit video"}
      icon={<Youtube size={18} />}
      footer={
        <div className="tw-flex tw-justify-end tw-gap-2">
          <AppButton variant="secondary" onClick={onClose} disabled={saving}>Cancel</AppButton>
          <AppButton onClick={submit} disabled={saving}>{saving ? "Saving…" : "Save"}</AppButton>
        </div>
      }
    >
      <form className="tw-flex tw-flex-col tw-gap-4" onSubmit={submit}>
        {error && <Callout tone="danger" role="alert">{error}</Callout>}

        <FormField label="Title" required>
          <Input value={form.title} onChange={(e) => set({ title: e.target.value })}
            placeholder="e.g. Attendance dashboard — 2 minute walkthrough" maxLength={200} autoFocus />
        </FormField>

        <FormField label="Video link" required hint="YouTube, Vimeo or any public video URL">
          <Input type="url" value={form.url} onChange={(e) => set({ url: e.target.value })}
            placeholder="https://www.youtube.com/watch?v=…" />
        </FormField>

        {projects.length > 0 && (
          <FormField label="Related project" hint="Optional — links this video to one of your projects">
            <NativeSelect value={form.projectId} onChange={(e) => set({ projectId: e.target.value })}>
              <option value="">Not linked</option>
              {projects.map((p) => <option key={p._id} value={p._id}>{p.title}</option>)}
            </NativeSelect>
          </FormField>
        )}

        <FormField label="Description">
          <Textarea rows={3} value={form.description}
            onChange={(e) => set({ description: e.target.value })} maxLength={1000} />
        </FormField>
      </form>
    </AppModal>
  );
};

export default VideosSection;
